import { Lock } from "lucide-react";

interface ReportPreviewProps {
  theme: string;
  episodeCount?: number;
  query: string;
  podcastArtworks?: string[];
}

export default function ReportPreview({ theme, query, podcastArtworks = [] }: ReportPreviewProps) {
  const displayTheme = theme || query || "ecommerce";

  return (
    <div className="relative flex-shrink-0 select-none" style={{ width: 88 }}>
      {/* Stacked pages — slight rotation for realism */}
      <div
        className="absolute inset-0 bg-muted border border-border/60 rounded-[2px]"
        style={{ transform: "translate(4px, 4px) rotate(2deg)", boxShadow: "2px 3px 8px hsl(var(--foreground) / 0.08)" }}
      />
      <div
        className="absolute inset-0 bg-muted/80 border border-border/40 rounded-[2px]"
        style={{ transform: "translate(2px, 2px) rotate(0.8deg)", boxShadow: "1px 2px 4px hsl(var(--foreground) / 0.05)" }}
      />

      {/* Main page — 8.5:11 aspect ratio */}
      <div
        className="relative bg-white border-2 border-border rounded-[2px] overflow-hidden flex flex-col"
        style={{
          aspectRatio: "8.5 / 11",
          boxShadow: "0 4px 12px hsl(var(--foreground) / 0.12), 0 1px 3px hsl(var(--foreground) / 0.08)",
        }}
      >
        {/* Branded header bar */}
        <div
          className="px-2 py-1 flex items-center justify-between flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, hsl(var(--foreground)), hsl(var(--foreground) / 0.85))",
          }}
        >
          <span className="font-mono text-[5px] sm:text-[6px] text-white font-bold tracking-wide uppercase">
            AskThePods
          </span>
          <div className="flex gap-[2px]">
            <div className="w-1 h-1 rounded-full bg-primary" />
            <div className="w-1 h-1 rounded-full bg-accent" />
          </div>
        </div>

        {/* Orange accent stripe */}
        <div className="h-[2px] flex-shrink-0" style={{ background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent)), hsl(var(--primary)))" }} />

        {/* Page content — flex-1 to fill available space */}
        <div className="flex-1 px-2 pt-1.5 pb-1 flex flex-col overflow-hidden">
          {/* Report title */}
          <div className="flex-shrink-0">
            <span className="font-mono text-[4px] sm:text-[5px] text-muted-foreground uppercase tracking-[0.12em] block mb-[1px]">
              Expert Briefing
            </span>
            <h4 className="font-retro font-bold text-[6px] sm:text-[7px] leading-tight text-foreground line-clamp-2">
              {displayTheme}
            </h4>
          </div>

          {/* Divider */}
          <div className="border-t border-border/60 my-1 flex-shrink-0" />

          {/* Top Episodes section */}
          <div className="flex-shrink-0">
            <span className="font-mono text-[3.5px] sm:text-[4px] text-primary font-bold uppercase tracking-wide block mb-[3px]">
              Top 3 Episodes
            </span>

            <div className="space-y-[3px]">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-[3px]">
                  {podcastArtworks[i] ? (
                    <img
                      src={podcastArtworks[i]}
                      alt=""
                      className="w-2.5 h-2.5 rounded-[1px] border border-border/50 flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div
                      className="w-2.5 h-2.5 rounded-[1px] border border-border/50 flex-shrink-0"
                      style={{ background: `hsl(var(--primary) / ${0.15 + i * 0.08})` }}
                    />
                  )}
                  <div className="flex-1 space-y-[1px]">
                    <div className="h-[2px] bg-foreground/20 rounded-full w-full" />
                    <div className="h-[1.5px] bg-foreground/10 rounded-full" style={{ width: `${75 - i * 10}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Blurred content tease — fills remaining space */}
          <div className="relative flex-1 mt-1 min-h-0">
            <div className="space-y-[2px] opacity-[0.08]" aria-hidden>
              <div className="h-[2px] bg-foreground rounded-full w-full" />
              <div className="h-[2px] bg-foreground rounded-full w-11/12" />
              <div className="h-[2px] bg-foreground rounded-full w-9/12" />
              <div className="h-[2px] bg-foreground rounded-full w-full" />
            </div>

            {/* Lock overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-[2px] bg-white/90 border border-border/60 px-1 py-[1px] rounded-[1px]">
                <Lock className="w-[5px] h-[5px] text-primary" />
                <span className="font-mono text-[3.5px] font-bold text-primary">
                  Unlock
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer bar — part of flex flow, not absolute */}
        <div className="flex-shrink-0 px-2 py-[2px] border-t border-border/40" style={{ background: "hsl(var(--muted) / 0.6)" }}>
          <span className="font-mono text-[3px] sm:text-[3.5px] text-muted-foreground">
            AskThePods AI · Expert Briefing
          </span>
        </div>
      </div>
    </div>
  );
}
